import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { HeadlineM, LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { ScarcityLabel } from '@sorare/core/src/components/collections/ScarcityLabel';
import { Score } from '@sorare/core/src/components/collections/Score';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { ensureHexColor, isLightColor } from '@sorare/core/src/lib/colors';
import { glossary } from '@sorare/core/src/lib/glossary';

import { SlotsPreview } from 'components/collections/SlotsPreview';

import { minimalCollectionLayout_cardCollectionFragment } from '../NewCollectionLayout/data';
import { SplitProgressBar } from '../SplitProgressBar';
import { CollectionPreviewStats } from './CollectionPreviewStats';
import { CollectionPreview_cardCollection } from './__generated__/index.graphql';

const completedColors: Partial<Record<Rarity, string>> = {
  [Rarity.limited]: '#ae5e13',
  [Rarity.rare]: '#c22342',
  [Rarity.super_rare]: '#366bf2',
  [Rarity.unique]: 'var(--c-collection-unique)',
};

const rotation = keyframes`
    from {
      --gradient-angle: 0deg;
    }
    to {
      --gradient-angle: 360deg;
    }
`;

const GradientWrapper = styled.div`
  position: relative;
  isolation: isolate;
  height: 100%;
  border-radius: var(--double-unit);

  @property --gradient-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }
  &.completed {
    &::before,
    &::after {
      content: '';
      position: absolute;
      border-radius: inherit;
      inset: -2px;
      background: conic-gradient(
        from var(--gradient-angle, 0deg),
        var(--completed-color),
        var(--c-nd-100-opaque),
        var(--completed-color)
      );
      z-index: -2;
      animation: ${rotation} 5s linear infinite;
    }
    &::after {
      filter: blur(10px);
    }
  }
`;

const Root = styled(Vertical).attrs({ gap: 2 })`
  background: linear-gradient(180deg, var(--c-nl-200), var(--c-nl-200)),
    linear-gradient(180deg, var(--background-color) 0%, var(--c-nl-800) 120%),
    var(--c-nd-300-opaque);
  padding: var(--double-unit) 0;
  border-radius: var(--double-unit);
  height: 100%;
`;
const StyledLink = styled(Link)`
  transition: transform 0.2s ease-in-out;
  &:hover,
  &:focus {
    color: inherit;
    transform: scale(1.02);
  }
`;
const StyledLinkBox = styled(LinkBox)`
  transition: transform 0.2s ease-in-out;
  &:hover,
  &:focus {
    color: inherit;
    transform: scale(1.02);
  }
`;
const EmptyCollectionRoot = styled.div`
  height: 100%;
  background: var(--c-nd-50);
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  display: grid;
  grid-template-rows: calc(5 * var(--unit)) 1fr calc(5 * var(--unit));
`;
const ImgWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  height: 80px;
  margin-bottom: var(--unit);
`;
const StyledImg = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
`;
const StyledSBHorizontal = styled(SBHorizontal)`
  align-items: flex-start;
`;
const WhiteColorWrapper = styled.span`
  color: var(--c-white);
`;
const VerticalWithLateralPadding = styled(Vertical)`
  padding: 0 var(--double-unit);
`;

const Wrapper = ({ children, to }: { children: ReactNode; to?: string }) => {
  if (to) {
    return <StyledLink to={to}>{children}</StyledLink>;
  }

  return children;
};

type Props = {
  collection: CollectionPreview_cardCollection;
  to?: string;
  shouldBeHighlighted?: boolean;
};

export const CollectionPreview = ({
  collection,
  to,
  shouldBeHighlighted,
}: Props) => {
  const {
    rarity,
    team,
    sportSeason,
    slotsCount,
    userCardCollection,
    bonusThresholds,
  } = collection;
  if (!team) return null;

  const completed = userCardCollection?.fulfilledSlotsCount === slotsCount;

  const { pictureUrl, name } = team;

  const { styleGuideColor } = team;
  const { startYear, endYear, singleCivilYear } = sportSeason || {};
  const displayedStartYear = singleCivilYear
    ? startYear
    : startYear && startYear % 2000;
  const displayedEndYear = !singleCivilYear && endYear ? endYear % 2000 : null;

  const backgroundColor =
    styleGuideColor && !isLightColor(ensureHexColor(styleGuideColor))
      ? styleGuideColor
      : 'var(--c-nl-700-opaque)';

  const nextThreshold = bonusThresholds.find(({ threshold }) => {
    return threshold > (userCardCollection?.score || 0);
  })?.threshold;

  const emptyCollection = !userCardCollection?.fulfilledSlotsCount;

  if (emptyCollection) {
    return (
      <StyledLinkBox>
        <EmptyCollectionRoot>
          <StyledSBHorizontal>
            <LabelS>
              <ScarcityLabel scarcity={rarity || Rarity.custom_series} />
            </LabelS>
            <Vertical gap={0}>
              <LabelL bold>{displayedStartYear}</LabelL>
              {displayedEndYear && <LabelL bold>{displayedEndYear}</LabelL>}
            </Vertical>
          </StyledSBHorizontal>
          <Vertical gap={2} center>
            <ImgWrapper>
              {pictureUrl && <StyledImg alt="" src={pictureUrl} />}
            </ImgWrapper>
            <Vertical gap={0} className="text-center">
              <HeadlineM>{name}</HeadlineM>
              <LabelL color="var(--c-nd-600)">
                <FormattedMessage
                  id="CollectionPreview.collectionBonus"
                  defaultMessage="Collection Bonus"
                />
              </LabelL>
            </Vertical>
            <LinkOverlay as={Button} size="small" color="quaternary" to={to}>
              <FormattedMessage
                id="CollectionPreview.emptyCollectionCta"
                defaultMessage="Start Collection"
              />
            </LinkOverlay>
          </Vertical>
        </EmptyCollectionRoot>
      </StyledLinkBox>
    );
  }

  return (
    <Wrapper to={to}>
      <GradientWrapper
        className={classNames({ completed })}
        style={
          {
            '--completed-color':
              (rarity && completedColors[rarity]) || '#ae5e13',
          } as CSSProperties
        }
      >
        <Root
          style={
            {
              '--background-color': backgroundColor,
            } as CSSProperties
          }
        >
          <VerticalWithLateralPadding>
            <StyledSBHorizontal>
              <Vertical gap={0}>
                <LabelS>
                  <ScarcityLabel scarcity={rarity || Rarity.custom_series} />
                </LabelS>
                {completed && (
                  <LabelS color="var(--c-rivals-win)">
                    <FormattedMessage {...glossary.completed} />
                  </LabelS>
                )}
              </Vertical>
              <Vertical gap={0}>
                <LabelL bold>{displayedStartYear}</LabelL>
                {displayedEndYear && <LabelL bold>{displayedEndYear}</LabelL>}
              </Vertical>
            </StyledSBHorizontal>
            <OnboardingHighlightWrapper
              highlightName="CollectionPreviewPicture"
              wrap={!!shouldBeHighlighted}
            >
              <ImgWrapper>
                {pictureUrl && <StyledImg alt="" src={pictureUrl} />}
              </ImgWrapper>
            </OnboardingHighlightWrapper>
          </VerticalWithLateralPadding>
          <Vertical gap={3}>
            <VerticalWithLateralPadding gap={1.5}>
              <Vertical gap={0} className="text-center">
                <OnboardingHighlightWrapper
                  highlightName="CollectionPreviewName"
                  wrap={!!shouldBeHighlighted}
                >
                  <HeadlineM className="text-center">{name}</HeadlineM>
                </OnboardingHighlightWrapper>
                <OnboardingHighlightWrapper
                  highlightName="CollectionPreviewCollectionBonusLabel"
                  wrap={!!shouldBeHighlighted}
                >
                  {!!bonusThresholds.length && (
                    <LabelL color="var(--c-nd-600)" className="text-center">
                      <FormattedMessage
                        id="CollectionPreview.collectionBonus"
                        defaultMessage="Collection Bonus"
                      />
                    </LabelL>
                  )}
                </OnboardingHighlightWrapper>
              </Vertical>
              <OnboardingHighlightWrapper
                highlightName="CollectionPreviewCollectionBonusProgressBar"
                wrap={!!shouldBeHighlighted}
              >
                {!!bonusThresholds.length && (
                  <SplitProgressBar
                    cardCollection={collection}
                    userCardCollection={collection.userCardCollection}
                  />
                )}
              </OnboardingHighlightWrapper>
            </VerticalWithLateralPadding>
            <CollectionPreviewStats
              slotsCount={slotsCount}
              userCardCollection={userCardCollection}
              shouldBeHighlighted={shouldBeHighlighted}
            />
            <SlotsPreview
              cardCollection={collection}
              shouldBeHighlighted={shouldBeHighlighted}
            />
          </Vertical>
          <VerticalWithLateralPadding>
            <SBHorizontal>
              <Horizontal>
                {nextThreshold && (
                  <LabelL color="var(--c-nd-600)">
                    <FormattedMessage
                      id="CollectionPreview.nextLevel"
                      defaultMessage="{score} to the next level"
                      values={{
                        score: (
                          <WhiteColorWrapper>
                            <Score
                              score={
                                nextThreshold - (userCardCollection?.score || 0)
                              }
                            />
                          </WhiteColorWrapper>
                        ),
                      }}
                    />
                  </LabelL>
                )}
              </Horizontal>
              <IconButton color="secondary" smaller>
                <ChevronRightBold width={12} height={12} />
              </IconButton>
            </SBHorizontal>
          </VerticalWithLateralPadding>
        </Root>
      </GradientWrapper>
    </Wrapper>
  );
};

CollectionPreview.fragments = {
  cardCollection: gql`
    fragment CollectionPreview_cardCollection on CardCollection {
      slug
      rarity
      slotsCount
      bonusThresholds {
        threshold
      }
      team: anyTeam {
        slug
        name
        pictureUrl
        styleGuideColor
      }
      sportSeason {
        name
        startYear
        endYear
        singleCivilYear
      }
      userCardCollection(forUserSlug: $user) {
        id
        fulfilledSlotsCount
        score
        ...SplitProgressBar_userCardCollection
        ...CollectionPreviewStats_userCardCollection
      }
      ...SplitProgressBar_cardCollection
      ...SlotsPreview_cardCollection
      ...minimalCollectionLayout_cardCollection
    }
    ${SplitProgressBar.fragments.cardCollection}
    ${SplitProgressBar.fragments.userCardCollection}
    ${CollectionPreviewStats.fragments.userCardCollection}
    ${SlotsPreview.fragments.cardCollection}
    ${minimalCollectionLayout_cardCollectionFragment}
  ` as TypedDocumentNode<CollectionPreview_cardCollection>,
};
